import React from "react";
import {
  Card,
  CardContent,
  Typography,
  Chip,
  Box,
  Stack,
  IconButton,
  Button,
  alpha,
  Slide,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import ThumbUpIcon from "@mui/icons-material/ThumbUp";
import ThumbsUpDownIcon from "@mui/icons-material/ThumbsUpDown";
import ThumbDownAltIcon from "@mui/icons-material/ThumbDownAlt";
import PaymentsIcon from "@mui/icons-material/Payments";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import RuleIcon from "@mui/icons-material/Rule";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import { SegmentedProgressBar } from "../../../shared/components";
import { GeospatialProject } from "../hooks/useGeospatialProjectsData";
import { useNavigation } from "../../../hooks/useNavigation";
import { formatCurrency } from "../../../shared/utils";

interface ProjectInfoCardProps {
  project: GeospatialProject | null;
  onClose: () => void;
}

export const ProjectInfoCard: React.FC<ProjectInfoCardProps> = ({
  project,
  onClose,
}) => {
  const { goToProjectDetails } = useNavigation();

  if (!project) return null;

  const getRagIcon = (rag?: string) => {
    switch (rag?.toLowerCase()) {
      case "red":
        return <ThumbDownAltIcon sx={{ color: "error.main", fontSize: 20 }} />;
      case "amber":
        return (
          <ThumbsUpDownIcon sx={{ color: "warning.main", fontSize: 20 }} />
        );
      case "green":
      default:
        return <ThumbUpIcon sx={{ color: "success.main", fontSize: 20 }} />;
    }
  };

  const getRagColor = (
    rag?: string
  ): "error.main" | "warning.main" | "success.main" => {
    switch (rag?.toLowerCase()) {
      case "red":
        return "error.main";
      case "amber":
        return "warning.main";
      case "green":
      default:
        return "success.main";
    }
  };

  const handleViewDetails = () => {
    goToProjectDetails(project.id, project.title);
  };

  return (
    <Slide direction="up" in={!!project} mountOnEnter unmountOnExit>
      <Card
        variant="outlined"
        sx={{
          position: "absolute",
          bottom: 24,
          left: 24,
          width: { xs: "calc(100% - 48px)", sm: 420 },
          maxHeight: "60vh",
          overflow: "auto",
          zIndex: 10,
          borderTopWidth: "4px",
          borderTopStyle: "solid",
          borderTopColor: getRagColor(project.rag?.overall),
          backgroundColor: (theme) =>
            theme.palette.mode === "dark"
              ? alpha(theme.palette.background.paper, 0.95)
              : alpha(theme.palette.background.paper, 0.98),
          backdropFilter: "blur(8px)",
          boxShadow: (theme) => theme.shadows[8],
        }}
      >
        <CardContent>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="flex-start"
            mb={1}
          >
            <Box flex={1} mr={1}>
              <Typography
                variant="subtitle1"
                fontWeight={600}
                sx={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  display: "-webkit-box",
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: "vertical",
                }}
              >
                {project.title}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {project.project_reference}
              </Typography>
            </Box>
            <Box display="flex" alignItems="center" gap={0.5}>
              {getRagIcon(project.rag?.overall)}
              <IconButton size="small" onClick={onClose}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
          </Box>

          <Box display="flex" alignItems="center" gap={0.5} mb={1}>
            <LocationOnIcon
              sx={{ fontSize: 16, color: "text.secondary" }}
            />
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                overflow: "hidden",
                whiteSpace: "nowrap",
                textOverflow: "ellipsis",
              }}
            >
              {project.address || "No location"}
            </Typography>
          </Box>

          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap mb={1.5}>
            <Chip
              label={project.programme}
              size="small"
              variant="outlined"
              color="primary"
            />
            {project.network && (
              <Chip label={project.network} size="small" variant="outlined" />
            )}
            {project.site && (
              <Chip label={project.site} size="small" variant="outlined" />
            )}
          </Stack>

          <Chip
            sx={{ borderRadius: 1, mb: 1.5 }}
            label={project.phase_name}
            size="small"
            variant="filled"
            color="default"
          />

          <Box display="flex" alignItems="center" gap={1} mb={1.5}>
            <SegmentedProgressBar
              phases={project.phases}
              progressPercentage={project.progress?.percentage || 0}
              status={
                (project.rag?.overall?.toLowerCase() === "red"
                  ? "red"
                  : project.rag?.overall?.toLowerCase() === "amber"
                  ? "amber"
                  : "green") as "green" | "red" | "amber"
              }
            />
          </Box>

          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            mb={2}
          >
            <Stack direction="row" spacing={1}>
              <PaymentsIcon
                sx={{
                  fontSize: 22,
                  color: getRagColor(project.rag?.cost),
                }}
              />
              <AccessTimeIcon
                sx={{
                  fontSize: 22,
                  color: getRagColor(project.rag?.time),
                }}
              />
              <RuleIcon
                sx={{
                  fontSize: 22,
                  color: getRagColor(project.rag?.quality),
                }}
              />
            </Stack>
          </Stack>

          <Box
            sx={{
              display: "flex",
              gap: 1,
              pt: 2,
              borderTop: "1px solid",
              borderColor: "divider",
            }}
          >
            <Card
              variant="outlined"
              sx={{
                flex: 1,
                p: 1.5,
                textAlign: "center",
              }}
            >
              <Typography variant="caption" color="text.secondary" display="block">
                Budget
              </Typography>
              <Typography variant="body2" fontWeight={600}>
                {formatCurrency(project.project_budget || 0)}
              </Typography>
            </Card>
            <Card
              variant="outlined"
              sx={{
                flex: 1,
                p: 1.5,
                textAlign: "center",
              }}
            >
              <Typography variant="caption" color="text.secondary" display="block">
                Spent
              </Typography>
              <Typography variant="body2" fontWeight={600} color="warning.main">
                {formatCurrency(project.actual_spend || 0)}
              </Typography>
            </Card>
            <Card
              variant="outlined"
              sx={{
                flex: 1,
                p: 1.5,
                textAlign: "center",
              }}
            >
              <Typography variant="caption" color="text.secondary" display="block">
                Progress
              </Typography>
              <Typography variant="body2" fontWeight={600} color="primary.main">
                {project.progress?.percentage || 0}%
              </Typography>
            </Card>
          </Box>

          <Button
            fullWidth
            variant="contained"
            endIcon={<OpenInNewIcon />}
            onClick={handleViewDetails}
            sx={{ mt: 2 }}
          >
            View Project Details
          </Button>
        </CardContent>
      </Card>
    </Slide>
  );
};

